import Flag from '@/components/Flag'
import DuoFunnelLayout from '@/components/v2/DuoFunnelLayout'
import DuoOptionRow from '@/components/v2/DuoOptionRow'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Form } from 'react-router-dom'
import type { StepScreenProps } from './types'

export interface DuoLanguageAnswer {
  emoji: string
  title: string
  value: string
}

/**
 * Duolingo's opening screen: "I want to learn…" over a 2-column grid of flag
 * cards, with the sticky CONTINUE disabled until one is picked. The tracked
 * field name is the English title and the value is the language code.
 */
export default function LanguageGridStep({
  answers,
  onAnswered,
  step,
  titleKey,
  totalSteps,
  withLogin,
}: StepScreenProps & {
  answers: DuoLanguageAnswer[]
  onAnswered?: (value: string) => void
  titleKey: string
  withLogin?: boolean
}) {
  const [selectedValue, setSelectedValue] = useState<string | undefined>(
    undefined,
  )

  const { t } = useTranslation()

  const fieldName = t(titleKey, { lng: 'en' })

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value

    setSelectedValue(value)
    onAnswered?.(value)
  }

  return (
    <Form method="post">
      <DuoFunnelLayout
        footer={
          <button
            className="duo-button-primary"
            data-testid="button-continue"
            disabled={!selectedValue}
            type="submit"
          >
            {t('shared.continue')}
          </button>
        }
        step={step}
        totalSteps={totalSteps}
        withLogin={withLogin}
      >
        <h1 className="duo-heading shrink-0">{t(titleKey)}</h1>

        <div className="mt-8 grid grow auto-rows-min grid-cols-2 gap-3">
          {answers.map((answer) => {
            const value = t(answer.value)

            return (
              <DuoOptionRow
                className="items-center px-3 py-3.5 text-[15px]"
                inputType="radio"
                key={answer.value}
                leading={<Flag emoji={answer.emoji} />}
                name={fieldName}
                onChange={handleChange}
                selected={selectedValue === value}
                title={t(answer.title)}
                value={value}
              />
            )
          })}
        </div>
      </DuoFunnelLayout>
    </Form>
  )
}
